import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  Search, 
  Target, 
  Video, 
  TrendingUp,
  ArrowRight,
  CheckCircle,
  Clock,
  BarChart3,
  Lightbulb,
  Settings,
  Eye,
  Zap
} from "lucide-react";

const Process = () => {
  const processSteps = [
    {
      step: "01",
      icon: <Search className="h-12 w-12" />,
      title: "Audit & Research",
      duration: "Week 1-2",
      description: "Deep dive analysis of your current performance, market position, and growth opportunities.",
      activities: [
        "Current performance audit",
        "Competitor analysis & benchmarking",
        "Target audience research",
        "Market opportunity assessment",
        "Technical setup review"
      ],
      deliverables: [
        "Comprehensive audit report",
        "Competitor landscape analysis",
        "Growth opportunity matrix",
        "Technical recommendations"
      ]
    },
    {
      step: "02",
      icon: <Target className="h-12 w-12" />,
      title: "Strategy & Media Planning",
      duration: "Week 2-3",
      description: "Develop comprehensive media strategy and creative framework tailored to your specific goals.",
      activities: [
        "Growth strategy development",
        "Media mix optimization",
        "Budget allocation planning",
        "Creative strategy framework",
        "KPI & tracking setup"
      ],
      deliverables: [
        "Growth marketing strategy",
        "Media planning document",
        "Creative brief & guidelines",
        "Performance tracking setup"
      ]
    },
    {
      step: "03",
      icon: <Video className="h-12 w-12" />,
      title: "Creative Production (Human + AI)",
      duration: "Week 3-4",
      description: "Create high-converting content using both human creativity and AI automation for scalable production.",
      activities: [
        "Creative concept development",
        "AI-powered content generation",
        "Video production & editing",
        "Ad copy & creative variations",
        "Landing page optimization"
      ],
      deliverables: [
        "Creative asset library",
        "Video content suite",
        "Ad copy variations",
        "Optimized landing pages"
      ]
    },
    {
      step: "04",
      icon: <TrendingUp className="h-12 w-12" />,
      title: "Launch, Test, Optimize, Scale",
      duration: "Ongoing",
      description: "Execute campaigns with continuous testing, optimization, and systematic scaling for maximum performance.",
      activities: [
        "Campaign launch & monitoring",
        "A/B testing & optimization",
        "Performance analysis",
        "Scaling winning campaigns",
        "Continuous improvement"
      ],
      deliverables: [
        "Live campaign management",
        "Weekly performance reports",
        "Optimization recommendations",
        "Scaling action plans"
      ]
    }
  ];

  const methodology = [
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Data-Driven Decisions",
      description: "Every optimization is backed by statistical significance and performance data."
    },
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: "Creative Testing Framework",
      description: "Systematic approach to testing and scaling high-performing creative assets."
    },
    {
      icon: <Settings className="h-8 w-8" />,
      title: "Continuous Optimization",
      description: "Daily monitoring and weekly optimization cycles for consistent improvement."
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Transparent Reporting",
      description: "Real-time dashboard access and detailed weekly performance reports."
    }
  ];

  const timeline = [
    { week: "Week 1-2", phase: "Discovery & Audit", focus: "Understanding your business and current performance" },
    { week: "Week 2-3", phase: "Strategy Development", focus: "Creating your custom growth framework" },
    { week: "Week 3-4", phase: "Creative Production", focus: "Building high-converting content assets" },
    { week: "Week 4+", phase: "Launch & Scale", focus: "Executing and optimizing for maximum ROI" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6">
              <Zap className="h-3 w-3 mr-1" />
              Proven Growth Framework
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Our <span className="text-gradient">Process</span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8">
              A systematic, data-driven approach to growth that delivers consistent, 
              measurable results for every client partnership.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="pb-20 lg:pb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {processSteps.map((step, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}>
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="text-4xl font-bold text-gradient">
                      {step.step}
                    </div>
                    <Badge variant="outline">{step.duration}</Badge>
                  </div>
                  
                  <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                    {step.title}
                  </h2>
                  
                  <p className="text-xl text-muted-foreground mb-8">
                    {step.description}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold mb-4 text-primary">Key Activities</h4>
                      <ul className="space-y-2">
                        {step.activities.map((activity, activityIndex) => (
                          <li key={activityIndex} className="flex items-start space-x-2">
                            <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                            <span className="text-sm">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-4 text-primary">Deliverables</h4>
                      <ul className="space-y-2">
                        {step.deliverables.map((deliverable, deliverableIndex) => (
                          <li key={deliverableIndex} className="flex items-start space-x-2">
                            <CheckCircle className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                            <span className="text-sm">{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                {/* Visual */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <Card className="card-elegant bg-gradient-to-br from-primary/5 to-primary-glow/5 border-primary/20">
                    <CardContent className="p-12 text-center">
                      <div className="text-primary mb-6 flex justify-center">
                        {step.icon}
                      </div>
                      <div className="text-6xl font-bold text-gradient mb-4">
                        {step.step}
                      </div>
                      <div className="text-xl font-semibold">
                        {step.title}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Our <span className="text-gradient">Methodology</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              The core principles that guide every decision and optimization in our growth process.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodology.map((item, index) => (
              <Card key={index} className="card-elegant text-center">
                <CardContent className="p-8">
                  <div className="text-primary mb-4 flex justify-center">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Project <span className="text-gradient">Timeline</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              What to expect in your first month and beyond.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <Card className="card-elegant flex-1">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                        <h3 className="text-xl font-semibold">{item.phase}</h3>
                        <Badge variant="outline">{item.week}</Badge>
                      </div>
                      <p className="text-muted-foreground">{item.focus}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="card-elegant bg-gradient-to-r from-primary/10 to-primary-glow/10 border-primary/20">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                Ready to <span className="text-gradient">Start Your Growth</span>?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Let's begin with a comprehensive audit of your current performance and growth opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="btn-glow text-lg px-8 py-6">
                  <Link to="/contact">
                    Get Your Free Audit
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6">
                  <Link to="/case-studies">
                    See Process in Action
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Process;